import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFeedback } from '@/components/feedback/feedback-context'
import { FormField } from '@/components/forms/form-field'
import { Input } from '@/components/forms/input'
import { Modal } from '@/components/overlays/modal'
import { Button } from '@/components/primitives/button'

import { createMemberAccount, type AdminMemberCreateResult } from './api'

interface AdminMemberCreateModalProps {
  onClose: () => void
  onCreated: (member: AdminMemberCreateResult) => void
  open: boolean
}

type CreateMemberErrors = Partial<Record<'email' | 'name', string>>

const emailPattern = /^[\w.!#$%&'*+\-/=?^{|}~]+@([\w-]+\.)+[\w-]{2,}$/i

export function AdminMemberCreateModal({ onClose, onCreated, open }: AdminMemberCreateModalProps) {
  const { t } = useTranslation('adminOrganization')
  const { showFeedback } = useFeedback()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [mustChangePassword, setMustChangePassword] = useState(true)
  const [errors, setErrors] = useState<CreateMemberErrors>({})

  const createMutation = useMutation({
    mutationFn: async () => {
      const nextErrors: CreateMemberErrors = {}
      if (!name.trim()) {
        nextErrors.name = t('memberAccounts.createModal.validation.nameRequired')
      }
      if (!email.trim()) {
        nextErrors.email = t('memberAccounts.createModal.validation.emailRequired')
      } else if (!emailPattern.test(email.trim())) {
        nextErrors.email = t('memberAccounts.createModal.validation.emailInvalid')
      }
      if (Object.keys(nextErrors).length > 0) {
        setErrors(nextErrors)
        return undefined
      }

      setErrors({})
      return createMemberAccount({ email, mustChangePassword, name })
    },
    onError: () => {
      showFeedback({
        message: t('errors.submitFailed'),
        tone: 'error',
      })
    },
    onSuccess: async (member) => {
      if (!member) {
        return
      }

      await queryClient.invalidateQueries({ queryKey: ['admin-organization-member-accounts'] })
      await queryClient.invalidateQueries({ queryKey: ['admin-organization-tree'] })
      showFeedback({
        message: t('success.createMemberAccount'),
        tone: 'success',
      })
      setName('')
      setEmail('')
      setMustChangePassword(true)
      onClose()
      onCreated(member)
    },
  })

  return (
    <Modal
      closeLabel={t('memberAccounts.createModal.close')}
      footer={
        <>
          <Button
            disabled={createMutation.isPending}
            onClick={onClose}
            size="sm"
            variant="secondary"
          >
            {t('actions.cancel')}
          </Button>
          <Button
            form="admin-member-create-form"
            isLoading={createMutation.isPending}
            size="sm"
            type="submit"
          >
            {t('memberAccounts.createModal.confirm')}
          </Button>
        </>
      }
      onClose={onClose}
      open={open}
      title={t('memberAccounts.createModal.title')}
    >
      <form
        className="grid gap-4"
        id="admin-member-create-form"
        onSubmit={(event) => {
          event.preventDefault()
          createMutation.mutate()
        }}
      >
        <FormField
          error={errors.name}
          htmlFor="admin-member-create-name"
          label={t('memberAccounts.createModal.name')}
          required
        >
          <Input
            autoComplete="name"
            id="admin-member-create-name"
            invalid={Boolean(errors.name)}
            onChange={(event) => {
              setName(event.target.value)
              setErrors((current) => ({ ...current, name: undefined }))
            }}
            placeholder={t('memberAccounts.createModal.namePlaceholder')}
            value={name}
          />
        </FormField>
        <FormField
          error={errors.email}
          htmlFor="admin-member-create-email"
          label={t('memberAccounts.createModal.email')}
          required
        >
          <Input
            autoComplete="email"
            id="admin-member-create-email"
            invalid={Boolean(errors.email)}
            onChange={(event) => {
              setEmail(event.target.value)
              setErrors((current) => ({ ...current, email: undefined }))
            }}
            placeholder={t('memberAccounts.createModal.emailPlaceholder')}
            type="email"
            value={email}
          />
        </FormField>
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-[var(--color-text)]">
          <input
            checked={mustChangePassword}
            className="size-3.5 accent-[var(--color-primary)]"
            onChange={(event) => setMustChangePassword(event.target.checked)}
            type="checkbox"
          />
          {t('memberAccounts.createModal.mustChangePassword')}
        </label>
      </form>
    </Modal>
  )
}
